import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  IconButton,
  Divider,
  CircularProgress,
  Modal,
  Tooltip,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, HelpOutline, Add, Remove } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz'; // Import for timezone formatting
import { setHours, setMinutes } from 'date-fns'; // Import date utility functions
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { UserAddress } from '../types';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import CheckoutSteps from '../components/CheckoutSteps';
import CheckoutItemSummary from '../components/CheckoutItemSummary';
import AddressForm from '../components/AddressForm';
import { formatMoney, getCasePrice } from '../utils/pricing';
import { 
  getCaseQuantityDisplayText, 
  getTotalItemCount, 
  createCartItemId,
  calculateCustomCasePrice,
  formatCasePrice,
  CUSTOM_CASE_FEE_CENTS 
} from '../utils/caseQuantity';

type DeliveryWindow = 'before_9am' | '9am_1pm' | '1pm_5pm' | 'after_5pm' | '';

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 400 },
  maxWidth: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  maxHeight: '90vh',
  overflow: 'auto',
};

const applyDeliveryWindowToDate = (date: Date, window: DeliveryWindow): Date => {
  const nextDate = new Date(date);

  switch (window) {
    case 'before_9am':
      return setMinutes(setHours(nextDate, 8), 0);
    case '9am_1pm':
      return setMinutes(setHours(nextDate, 9), 0);
    case '1pm_5pm':
      return setMinutes(setHours(nextDate, 13), 0);
    case 'after_5pm':
      return setMinutes(setHours(nextDate, 17), 0);
    default:
      return setMinutes(setHours(nextDate, 10), 0);
  }
};

const CartPage: React.FC = () => {
  const { cart, removeFromCart, updateCartItemQuantity, getCartTotal, createOrder, loading: cartLoading } = useCart();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const PACIFIC_TIMEZONE = 'America/Vancouver'; // Define Pacific timezone

  // View state
  const [viewMode, setViewMode] = useState<'cart' | 'checkout'>('cart');

  // Address state
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(true);
  
  // Checkout state
  const [activeStep, setActiveStep] = useState(0);
  const [deliveryOption, setDeliveryOption] = useState<'delivery' | 'pickup'>('delivery');
  const [returnOption, setReturnOption] = useState<'pickup' | 'self_return' | 'with_next_delivery'>('pickup');
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<number | ''>('');
  const [deliveryDateTime, setDeliveryDateTime] = useState<Date | null>(null);
  const [deliveryTimeWindow, setDeliveryTimeWindow] = useState<DeliveryWindow>('');
  const [returnPickupDateTime, setReturnPickupDateTime] = useState<Date | null>(null); // Add return pickup date state
  const [returnPickupTimeWindow, setReturnPickupTimeWindow] = useState<DeliveryWindow>('');
  const [selectedTrustedContact, setSelectedTrustedContact] = useState<number | ''>('');
  const [useShippingForBilling, setUseShippingForBilling] = useState(true);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [shippingCost, setShippingCost] = useState(0);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [estimatedDistance, setEstimatedDistance] = useState<number | null>(null);
  const [shippingZones, setShippingZones] = useState<any[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<string | null>(user?.payment_type || null);
  const [contractAccepted, setContractAccepted] = useState(false);

	// Sync payment method from user profile when the user object loads (handles async auth)
	useEffect(() => {
		if (user?.payment_type && !paymentMethod) {
			setPaymentMethod(user.payment_type);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.payment_type]);
  const [contactPreference, setContactPreference] = useState(false);
  const [showChargesAlertModal, setShowChargesAlertModal] = useState(false);
  const [lastAlertedDateTime, setLastAlertedDateTime] = useState<string | null>(null);
  const [hasManuallySelectedDate, setHasManuallySelectedDate] = useState(false);

  const fetchAddresses = useCallback(async () => {
    if (user) {
      try {
        setLoadingAddresses(true);
        const response = await api.get('/addresses');
        setAddresses(response.data);
        if (response.data.length > 0) {
          setSelectedShippingAddress(response.data[0].id);
          setSelectedBillingAddress(response.data[0].id);
        }
      } catch (error) {
        toast.error("Could not load your addresses.");
      } finally {
        setLoadingAddresses(false);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchAddresses();

    const fetchShippingZones = async () => {
      try {
        const response = await api.get('/shipping/zones');
        setShippingZones(response.data.zones);
      } catch (error) {
        console.error("Error fetching shipping zones:", error);
        toast.error("Could not load shipping zone information.");
      }
    };
    fetchShippingZones();
  }, [fetchAddresses]);
  
  useEffect(() => {
    const calculateShipping = async () => {
      // Only set to 0 if both delivery and return are self-service
      if (deliveryOption === 'pickup' && (returnOption === 'self_return' || returnOption === 'with_next_delivery')) {
        setShippingCost(0);
        return;
      }

      const address = addresses.find(addr => addr.id === selectedShippingAddress);
      if (!address || !address.postal_code) {
        setShippingCost(0);
        return;
      }

      try {
        setLoadingShipping(true);
        const response = await api.post('/shipping/calculate', {
          postal_code: address.postal_code,
          delivery_option: deliveryOption,
          delivery_datetime_str: deliveryDateTime ? deliveryDateTime.toISOString() : null,
          return_option: returnOption,
          address_id: selectedShippingAddress,
        });
        setShippingCost(Math.round(response.data.price * 100));
        setEstimatedDistance(response.data.distance_km);
      } catch (error) {
        console.error("Error calculating shipping cost:", error);
        toast.error("Could not calculate shipping cost. Please check your address.");
        setShippingCost(0);
      } finally {
        setLoadingShipping(false);
      }
    };

    calculateShipping();
  }, [deliveryOption, selectedShippingAddress, addresses, deliveryDateTime, returnOption]); // Added deliveryDateTime to dependencies

  useEffect(() => {
    // For pickup, we always need a separate billing address
    if (deliveryOption === 'pickup') {
      setUseShippingForBilling(false);
    }
  }, [deliveryOption]);

  // Show alert modal when afterhours or long-distance delivery is selected
  useEffect(() => {
    if (!deliveryDateTime || viewMode !== 'checkout' || !hasManuallySelectedDate) return;
    
    const isAfterHours = !(deliveryDateTime.getDay() >= 1 && deliveryDateTime.getDay() <= 5 && deliveryDateTime.getHours() >= 9 && deliveryDateTime.getHours() < 16);
    const isLongDistance = estimatedDistance !== null && estimatedDistance > 12;
    
    // Create a unique key for this delivery date/time and conditions
    const currentDateTimeKey = `${deliveryDateTime.toISOString()}-${isLongDistance}`;
    
    // Show alert if: (afterhours OR long-distance) AND haven't shown for this exact date/time/conditions
    if ((isAfterHours || isLongDistance) && lastAlertedDateTime !== currentDateTimeKey) {
      setShowChargesAlertModal(true);
      setLastAlertedDateTime(currentDateTimeKey);
    }
  }, [viewMode, deliveryDateTime, estimatedDistance, lastAlertedDateTime, hasManuallySelectedDate]);

  const handleAddressSubmit = async (addressData: Omit<UserAddress, 'id' | 'user_id'>) => {
    try {
      await toast.promise(api.post('/addresses', addressData), {
        loading: 'Creating address...',
        success: 'Address created successfully!',
        error: 'Failed to create address.'
      });
      setIsAddressModalOpen(false);
      fetchAddresses();
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleNextStep = async () => {
    if (activeStep === 3) { // Is last step
      if (!cart) return;

      const orderItems = cart.items.map(item => ({
        monorepo_reusable_type_id: item.product.id,
        quantity: getTotalItemCount(item.caseQuantity, item.numberOfCases),
        product_snapshot: {
          ...item.product,
          selected_case_quantity: item.caseQuantity,
          number_of_cases: item.numberOfCases,
          is_custom_case: item.isCustomCase || false,
        },
      }));

      try {
        await toast.promise(
          createOrder({
            shipping_address_id: deliveryOption === 'delivery' ? (selectedShippingAddress as number) : (selectedBillingAddress as number), // For pickup, billing is used as shipping
            billing_address_id: useShippingForBilling && deliveryOption === 'delivery' ? (selectedShippingAddress as number) : (selectedBillingAddress as number),
            trusted_contact_id: selectedTrustedContact ? (selectedTrustedContact as number) : undefined,
            delivery_option: deliveryOption,
            delivery_datetime: deliveryOption === 'delivery' ? (deliveryDateTime ? formatInTimeZone(deliveryDateTime, PACIFIC_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX") : null) : null,
            pickup_datetime: deliveryOption === 'pickup' ? (deliveryDateTime ? formatInTimeZone(deliveryDateTime, PACIFIC_TIMEZONE, "yyyy-MM-dd'T'HH:mm:ssXXX") : null) : null,
            return_method: returnOption === 'self_return' ? 'self_drop_off' : returnOption,
            return_pickup_datetime: (returnOption === 'pickup' || returnOption === 'self_return') ? (returnPickupDateTime ? returnPickupDateTime.toISOString() : null) : null,
            notes: notes,
            shipping_cost: shippingCost,
            tax_amount: tax,
            payment_method: paymentMethod,
            contact_preference: contactPreference,
            items: orderItems,
          }), {
            loading: 'Placing your order...',
            success: 'Order placed successfully!',
            error: 'There was an error placing your order.'
          }
        );
		// Persist the chosen payment method to the user's profile for next time
		if (paymentMethod && paymentMethod !== user?.payment_type) {
			try {
				await updateUser({ payment_type: paymentMethod });
			} catch {
				/* non-critical */
			}
		}
        navigate('/orders');
      } catch (error) { /* Toast will handle error */ }
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBackStep = () => {
    if (activeStep === 0) {
      setViewMode('cart');
    } else {
      setActiveStep(prev => prev - 1);
    }
  };

  const handleDeliveryOptionChange = (option: 'delivery' | 'pickup') => {
    setDeliveryOption(option);
    if (option === 'pickup') {
      setDeliveryDateTime(null);
      setDeliveryTimeWindow('');
      setHasManuallySelectedDate(false);
    }
  };

  const handleReturnOptionChange = (option: 'pickup' | 'self_return' | 'with_next_delivery') => {
    setReturnOption(option);
    // Clear return pickup date if with_next_delivery
    if (option === 'with_next_delivery') {
      setReturnPickupDateTime(null);
      setReturnPickupTimeWindow('');
    }
  };

  const handleDeliveryDateTimeChange = (date: Date | null) => {
    // Update the date value for the picker display, but don't mark as confirmed yet
    setDeliveryDateTime(date ? applyDeliveryWindowToDate(date, deliveryTimeWindow) : null);
  };

  const handleDeliveryDateTimeAccept = (date: Date | null) => {
    // Only mark as manually selected when user confirms (presses OK)
    setDeliveryDateTime(date ? applyDeliveryWindowToDate(date, deliveryTimeWindow) : null);
    setHasManuallySelectedDate(true);
  };

  const handleDeliveryTimeWindowChange = (window: DeliveryWindow) => {
    setDeliveryTimeWindow(window);
    setHasManuallySelectedDate(true);
    setDeliveryDateTime((currentDate) => currentDate ? applyDeliveryWindowToDate(currentDate, window) : currentDate);
  };

  const handleReturnPickupDateTimeChange = (date: Date | null) => { // Add return pickup date handler
    setReturnPickupDateTime(date ? applyDeliveryWindowToDate(date, returnPickupTimeWindow) : null);
  };

  const handleReturnPickupTimeWindowChange = (window: DeliveryWindow) => {
    setReturnPickupTimeWindow(window);
    setReturnPickupDateTime((currentDate) => currentDate ? applyDeliveryWindowToDate(currentDate, window) : currentDate);
  };

  if (cartLoading) return <CircularProgress />;
  if (!cart || cart.items.length === 0) {
    return (
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Your cart is empty.</Typography>
        <Button variant="outlined" onClick={() => navigate('/products')} sx={{ mt: 2 }}>
          Continue Shopping
        </Button>
      </Paper>
    );
  }

  const subtotal = getCartTotal();
  const tax = subtotal * 0.05;
  // Only include shipping cost in total when in checkout mode and shipping cost is available
  const total = subtotal + tax + (viewMode === 'checkout' && shippingCost > 0 ? shippingCost : 0);

  const selectedAddress = addresses.find(addr => addr.id === selectedShippingAddress);
  const preapprovedShipping = !!(selectedAddress && (selectedAddress.approved_delivery_fee || selectedAddress.approved_delivery_fee_non_standard));

  const isAfterHoursOrFarDistance = viewMode === 'checkout' && deliveryDateTime && hasManuallySelectedDate && (
    !(deliveryDateTime.getDay() >= 1 && deliveryDateTime.getDay() <= 5 && deliveryDateTime.getHours() >= 9 && deliveryDateTime.getHours() < 16) ||
    (estimatedDistance !== null && estimatedDistance > 12)
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
        {viewMode === 'cart' ? 'Shopping Cart' : 'Checkout'}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        <Paper sx={{ p: { xs: 2, md: 3 }, flex: '1 1 65%' }}>
          {viewMode === 'cart' ? (
            <>
              <Typography variant="h6" gutterBottom>
                {`Your Items (${cart.items.length})`}
              </Typography>
              <List>
                {cart.items.map((item, index) => {
                  const cartItemId = createCartItemId(item.productId, item.caseQuantity);
                  const casePrice = item.isCustomCase 
                    ? calculateCustomCasePrice(item.product, item.caseQuantity)
                    : getCasePrice(item.product, item.caseQuantity);
                  const totalItemCount = getTotalItemCount(item.caseQuantity, item.numberOfCases);
                  
                  return (
                    <ListItem 
                      key={cartItemId} 
                      divider 
                      sx={{
                        alignItems: 'flex-start', 
                        py: 3,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <Box 
                        sx={{
                          display: 'flex', 
                          flexGrow: 1, 
                          alignItems: 'flex-start', 
                          cursor: 'pointer', 
                          gap: 2,
                          flexDirection: { xs: 'column', sm: 'row' },
                          textAlign: { xs: 'center', sm: 'left' }
                        }}
                        onClick={() => navigate(`/products/${item.productId}`)}
                      >
                        <img 
                          src={item.product.image_url || '/placeholder-product.png'} 
                          alt={item.product.name} 
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }} 
                        />
                        <Box sx={{ flex: 1, pt: { xs: 1, sm: 0 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography variant="h6" fontWeight="medium">
                              {item.product.name}
                            </Typography>
                            {item.isCustomCase && (
                              <Chip 
                                label="Custom Case" 
                                size="small" 
                                color="warning"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                          <Typography 
                            variant="body1" 
                            color="primary" 
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {getCaseQuantityDisplayText(item.caseQuantity)}
                            {item.isCustomCase && ' (Custom)'}
                          </Typography>
                          <Typography color="text.secondary" variant="body2" sx={{ mb: 0.5 }}>
                            {formatMoney(casePrice)} per case
                            {item.isCustomCase && (
                              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main' }}>
                                (includes {formatCasePrice(CUSTOM_CASE_FEE_CENTS)} custom packing fee)
                              </Typography>
                            )}
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            Total: {totalItemCount} items
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Case Quantity Controls */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, ml: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Cases
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => updateCartItemQuantity(item.productId, item.caseQuantity, item.numberOfCases - 1)}
                            disabled={item.numberOfCases <= 1}
                          >
                            <Remove />
                          </IconButton>
                          <Typography sx={{ mx: 2, minWidth: 20, textAlign: 'center' }}>
                            {item.numberOfCases}
                          </Typography>
                          <IconButton 
                            size="small" 
                            onClick={() => updateCartItemQuantity(item.productId, item.caseQuantity, item.numberOfCases + 1)}
                          >
                            <Add />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <IconButton 
                        onClick={() => removeFromCart(item.productId, item.caseQuantity)} 
                        sx={{ ml: 2 }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  );
                })}
              </List>
            </>
          ) : (
            <CheckoutSteps
              activeStep={activeStep}
              addresses={addresses}
              deliveryOption={deliveryOption}
              selectedShippingAddress={selectedShippingAddress}
              deliveryDateTime={deliveryDateTime}
              deliveryTimeWindow={deliveryTimeWindow}
              returnPickupDateTime={returnPickupDateTime} // Add return pickup date prop
              returnPickupTimeWindow={returnPickupTimeWindow}
              selectedTrustedContact={selectedTrustedContact}
              onTrustedContactChange={setSelectedTrustedContact}
              useShippingForBilling={useShippingForBilling}
              selectedBillingAddress={selectedBillingAddress}
              notes={notes}
              onDeliveryOptionChange={handleDeliveryOptionChange}
              onShippingAddressChange={setSelectedShippingAddress}
              onDeliveryDateTimeChange={handleDeliveryDateTimeChange} // Renamed prop
              onDeliveryDateTimeAccept={handleDeliveryDateTimeAccept}
              onDeliveryTimeWindowChange={handleDeliveryTimeWindowChange}
              onReturnPickupDateTimeChange={handleReturnPickupDateTimeChange} // Add return pickup date handler
              onReturnPickupTimeWindowChange={handleReturnPickupTimeWindowChange}
              onUseShippingForBillingChange={setUseShippingForBilling}
              onBillingAddressChange={setSelectedBillingAddress}
              onNotesChange={setNotes}
              onAddNewAddress={() => setIsAddressModalOpen(true)}
              handleBack={handleBackStep}
              handleNext={handleNextStep}
              paymentMethod={paymentMethod}
              onPaymentMethodChange={setPaymentMethod}
              loadingShipping={loadingShipping}
              returnOption={returnOption}
              onReturnOptionChange={handleReturnOptionChange}
              contractAccepted={contractAccepted}
              onContractAcceptedChange={setContractAccepted}
              contactPreference={contactPreference}
              onContactPreferenceChange={setContactPreference}
              preapprovedShipping={preapprovedShipping}
            />
          )}
        </Paper>

        <Box sx={{ flex: '1 1 35%', position: 'sticky', top: '20px' }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            {viewMode === 'checkout' && (
              <>
                <CheckoutItemSummary />
                <Divider sx={{ my: 2 }} />
              </>
            )}
            <Typography variant="h6" sx={{ mb: 2 }}>Order Summary</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Subtotal</Typography>
              <Typography>{formatMoney(subtotal)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography>Tax (G)</Typography>
              <Typography>{formatMoney(tax)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography>
                  {(() => {
                    if (viewMode === 'checkout') {
                      const selectedAddress = addresses.find(addr => addr.id === selectedShippingAddress);
                      const hasApprovedFees = selectedAddress && (selectedAddress.approved_delivery_fee || selectedAddress.approved_delivery_fee_non_standard);
                      return hasApprovedFees ? 'Shipping' : 'Estimated Shipping';
                    }
                    return 'Estimated Shipping';
                  })()}
                </Typography>
                {viewMode === 'checkout' && estimatedDistance !== null && shippingZones.length > 0 && (
                  <Tooltip title={
                    <>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 1 }}>
                        Use the postal code and day/time to determine the pricing tier added to Summary
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        Estimated Distance: {estimatedDistance.toFixed(2)} km
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        Self Pickup or Self Return = free
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        &lt;4km, Mon-Fri, 9am-4pm = $9.99
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        &lt;4km, outside Mon-Fri, 9am-4pm = $19.99
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        4km-12km, Mon-Fri, 9am-4pm = $19.99
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        4km-12km, outside Mon-Fri, 9am-4pm = $29.99
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        Requests &gt;4km, afterhours and weekends/holidays are subject to approval
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block', mb: 0.5 }}>
                        For same-day delivery, add your request in the order notes
                      </Typography>
                      <Typography variant="caption" color="inherit" sx={{ display: 'block' }}>
                        &gt;12km = $29.99
                      </Typography>
                    </>
                  }>
                    <HelpOutline sx={{ ml: 0.5, fontSize: '1rem', color: 'text.secondary', cursor: 'pointer' }} />
                  </Tooltip>
                )}
              </Box>
              <Typography>
                {viewMode === 'cart' 
                  ? 'TBD' 
                  : loadingShipping 
                    ? <CircularProgress size={16} /> 
                    : (shippingCost > 0 ? formatMoney(shippingCost) : 'Free')
                }
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, typography: 'h6' }}>
              <Typography variant="h6">Total</Typography>
              <Typography variant="h6">{formatMoney(total)}</Typography>
            </Box>

            {viewMode === 'checkout' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  Important Notice:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total does not include Deposits on each item, estimated Refunds for their return after, or Loss Fees, which will appear on Invoices. If return systems are optimized, target return rates are 95% (equivalent to 5% loss).
                </Typography>
              </Box>
            )}

            {viewMode === 'checkout' && deliveryDateTime && hasManuallySelectedDate && (() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const delivery = new Date(deliveryDateTime);
              delivery.setHours(0, 0, 0, 0);
              const daysDiff = Math.floor((delivery.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              return daysDiff <= 1;
            })() && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                Next-day or same-day service might incur extra charges. For same-day delivery, please add your request in the order notes.
              </Typography>
            )}
            
            {viewMode === 'checkout' && isAfterHoursOrFarDistance && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
                Afterhours and/or long-distance service charges are added to the estimated shippment.
              </Typography>
            )}

            {viewMode === 'cart' && (
              <Button
                variant="outlined"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => setViewMode('checkout')}
                disabled={loadingAddresses}
              >
                {loadingAddresses ? 'Loading...' : 'Proceed'}
              </Button>
            )}
          </Paper>
        </Box>
      </Box>
      <Modal open={isAddressModalOpen} onClose={() => setIsAddressModalOpen(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            Add New Address
          </Typography>
          <AddressForm
            onSubmit={handleAddressSubmit}
            onCancel={() => setIsAddressModalOpen(false)}
          />
        </Box>
      </Modal>
      
      {/* Extra Charges Alert Modal */}
      <Modal open={showChargesAlertModal} onClose={() => setShowChargesAlertModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
            Additional Service Charges Apply
          </Typography>
          <Box sx={{ mb: 3 }}>
            {(() => {
              if (!deliveryDateTime) return null;
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const deliveryDate = new Date(deliveryDateTime);
              deliveryDate.setHours(0, 0, 0, 0);
              const daysDiff = Math.floor((deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
              
              if (daysDiff === 0) {
                return (
                  <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
                    • Same-day delivery may not be available. Please add your request in the order notes and we'll try to accommodate.
                  </Typography>
                );
              } else if (daysDiff === 1) {
                return (
                  <Typography variant="body1" sx={{ mb: 2, color: 'text.primary' }}>
                    • Next-day service might incur extra charges.
                  </Typography>
                );
              }
              return null;
            })()}
            <Typography variant="body1" sx={{ color: 'text.primary' }}>
              • Afterhours and/or long-distance service charges are added to the estimated shipment.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
            {(() => {
              const isAfterHoursTime = deliveryDateTime && !(deliveryDateTime.getDay() >= 1 && deliveryDateTime.getDay() <= 5 && deliveryDateTime.getHours() >= 9 && deliveryDateTime.getHours() < 16);
              const isLongDist = estimatedDistance !== null && estimatedDistance > 12;
              
              if (isLongDist && isAfterHoursTime) {
                return `Your delivery distance (${estimatedDistance?.toFixed(2)} km) exceeds our standard service area and your selected time is outside our standard business hours (Mon-Fri, 9am-4pm).`;
              } else if (isLongDist) {
                return `Your delivery distance (${estimatedDistance?.toFixed(2)} km) exceeds our standard service area.`;
              } else {
                return 'Your selected delivery time is outside our standard business hours (Mon-Fri, 9am-4pm).';
              }
            })()}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setShowChargesAlertModal(false)}
            sx={{ mt: 1 }}
          >
            I Understand
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default CartPage; 